import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import ModelPage from "@/pages/project/ModelPage";

const ProjectContext = createContext(null);
export const useProject = () => useContext(ProjectContext);

const TABS = [
  { to: "overview", label: "Overview" },
  { to: "model", label: "Model" },
  { to: "planning", label: "Planning" },
  { to: "factory", label: "Factory" },
  { to: "panels", label: "Panel List" },
  { to: "bom", label: "BOM" },
  { to: "properties", label: "Model Properties" },
  { to: "logistics", label: "Logistics & Shipments" },
  { to: "issues", label: "Issues" },
];

export default function ProjectLayout() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      setProject(data);
    } catch (err) {
      setError(apiError(err));
      toast.error(apiError(err));
    }
  }, [projectId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo(() => ({ project, projectId, reload }), [project, projectId, reload]);

  const { pathname } = useLocation();
  const onModelTab = pathname.endsWith("/model");
  const [modelMounted, setModelMounted] = useState(false);

  useEffect(() => {
    if (onModelTab) setModelMounted(true);
  }, [onModelTab]);

  if (error)
    return (
      <div className="min-h-screen bg-[#EEF2F8]">
        <TopBar />
        <div
          data-testid="project-access-error"
          className="mx-auto mt-24 max-w-md rounded-xl border border-red-500/30 bg-red-500/10 p-8 text-center text-sm text-red-700"
        >
          {error}
        </div>
      </div>
    );

  return (
    <ProjectContext.Provider value={value}>
      <div className="flex h-screen flex-col overflow-hidden bg-[#EEF2F8]">
        <TopBar subtitle={project ? `${project.code} · ${project.name}` : "Loading…"} />
        <nav className="okn-scroll flex shrink-0 gap-1 overflow-x-auto border-b border-slate-200 bg-white/92 px-4 backdrop-blur-md sm:px-6">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              data-testid={`project-tab-${tab.to}`}
              style={
                tab.to === "factory"
                  ? {
                      textShadow:
                        "0 0 1px rgba(8,145,178,.7), 0 0 8px rgba(34,211,238,.5)",
                    }
                  : undefined
              }
              className={({ isActive }) =>
                `mono relative shrink-0 whitespace-nowrap px-3 py-3 text-[10.5px] uppercase tracking-[0.14em] transition-colors [@media(max-height:520px)]:py-1.5 sm:px-4 sm:text-[11px] sm:tracking-[0.16em] ${
                  tab.to === "factory" ? "font-bold " : ""
                }${
                  isActive
                    ? "text-cyan-700 after:absolute after:inset-x-3 after:bottom-0 after:h-[2px] after:bg-cyan-400"
                    : tab.to === "factory"
                      ? "text-cyan-700 hover:text-cyan-500"
                      : "text-slate-500 hover:text-slate-900"
                }`
              }
            >
              {tab.label}
            </NavLink>
          ))}
          <NavLink
            to="action"
            data-testid="project-tab-action"
            className={({ isActive }) =>
              // Sticky only from lg up: on phones/tablets it would cover the
              // tabs it floats over while the bar scrolls horizontally.
              `mono group my-1.5 ml-auto flex shrink-0 items-center gap-2 self-center whitespace-nowrap rounded-full px-4 py-1.5 text-[10.5px] uppercase tracking-[0.14em] transition-all duration-200 sm:text-[11px] sm:tracking-[0.16em] lg:sticky lg:right-0 lg:shadow-[-14px_0_14px_10px_rgba(255,255,255,0.95)] ${
                isActive
                  ? "bg-cyan-600 text-white shadow-[0_0_0_3px_rgba(8,145,178,0.18)]"
                  : "bg-cyan-500 text-white shadow-[0_2px_10px_-2px_rgba(8,145,178,0.6)] hover:bg-cyan-600 hover:shadow-[0_4px_16px_-2px_rgba(8,145,178,0.7)]"
              }`
            }
          >
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/80" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
            Action
          </NavLink>
        </nav>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* The 3D viewer stays mounted once visited so tab switches never reload the model. */}
          {modelMounted ? (
            <div className={onModelTab ? "flex min-h-0 flex-1 flex-col" : "hidden"}>
              <ModelPage />
            </div>
          ) : null}
          {onModelTab ? null : <Outlet />}
        </div>
      </div>
    </ProjectContext.Provider>
  );
}
